import { apiRequest } from './api';
import type { Contact, ContactStatus } from '@/types/contacts';
import { STATUS_LABELS } from '@/lib/status';
import { getSourceLabel, getChannelLabel } from '@/lib/attributionDisplay';
import type { DateRange } from 'react-day-picker';

// ──────────────────────────────────────────────────────────────────────
// Contact mutation API
// ──────────────────────────────────────────────────────────────────────

export interface TransitionPayload {
  to_status: ContactStatus;
  value?: number;
  notes?: string;
}

export interface ConversionResult {
  platform: string;
  event_type: string;
  status: 'sent' | 'failed' | 'skipped';
  note: string | null;
}

export interface TransitionResponse {
  contact: Partial<Contact> & { id: string; status: ContactStatus };
  conversions_fired: ConversionResult[];
}

export async function transitionContact(
  contactId: string,
  payload: TransitionPayload,
): Promise<TransitionResponse> {
  return apiRequest<TransitionResponse>(`/contacts/${contactId}/transition`, {
    method: 'POST',
    body: payload as unknown as Record<string, unknown>,
  });
}

export interface ContactPatch {
  value?: number;
  notes?: string;
}

export async function updateContact(
  contactId: string,
  payload: ContactPatch,
): Promise<Partial<Contact> & { id: string }> {
  return apiRequest<Partial<Contact> & { id: string }>(`/contacts/${contactId}`, {
    method: 'PATCH',
    body: payload as unknown as Record<string, unknown>,
  });
}

// ──────────────────────────────────────────────────────────────────────
// Contacts table / filter layer (extracted from ContactsPage 2026-06-06)
// ──────────────────────────────────────────────────────────────────────

export const PAGE_SIZE = 15;
// Single empty-value placeholder so it never drifts to a plain hyphen.
export const EMPTY = '—';
// Brand sheet §4 time strings are en-GB (DD MMM ordering). Single source for
// the contacts date/CSV formatters below.
export const UI_LOCALE = 'en-GB';

export type DatePreset =
  | '24h'
  | '7d'
  | '30d'
  | 'month'
  | 'lastmonth'
  | 'all'
  | 'custom';

// Segment chip ids — internal tokens that drive `segmentMatches`. The status
// ids match the ContactStatus tokens directly so the predicate reads cleanly.
export type Segment =
  | 'all'
  | 'purchase'
  | 'qualified'
  | 'new_lead'
  | 'meta'
  | 'google';

// Sortable columns whitelist — must match api/routes/contacts.py
// ALLOWED_SORT_FIELDS. Non-sortable columns (Line / Client / Conversion) are
// not in this union; they carry `sortable: false` in CONTACTS_COLUMNS.
export type SortField =
  | 'created_at'
  | 'phone'
  | 'status'
  | 'source'
  | 'channel'
  | 'campaign';

export interface ContactsListResponse {
  items: Contact[];
  next_cursor: string | null;
}

export const DATE_CHIPS: { id: DatePreset; label: string }[] = [
  { id: '24h', label: '24 hours' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'month', label: 'This month' },
  { id: 'lastmonth', label: 'Last month' },
  { id: 'all', label: 'All time' },
];

// Segment labels — the 3 status segments display the same as the row pills
// (`STATUS_LABELS`). Source segments use platform names.
export const SEGMENTS: { id: Segment; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'purchase', label: STATUS_LABELS.purchase },
  { id: 'qualified', label: STATUS_LABELS.qualified },
  { id: 'new_lead', label: STATUS_LABELS.new_lead },
  { id: 'meta', label: 'From Meta Ads' },
  { id: 'google', label: 'From Google Ads' },
];

// ─── Column single-source ─────────────────────────────────────────────
// One definition of the table columns so the header row, colSpan, and
// skeleton never drift (the old `5 + ... + 2` formula was copied in 3 places
// and broke when C8 added the Conversion column). The config drives the
// column LIST — existence, order, label, sortability — not a cell renderer;
// ContactRow keeps explicit per-cell JSX (cells are heterogeneous).
export type ContactColumnId =
  | 'created_at'
  | 'phone'
  | 'source'
  | 'channel'
  | 'campaign'
  | 'line'
  | 'client'
  | 'conversion'
  | 'status';

export interface ColVis {
  showClient: boolean;
  showYourNumber: boolean;
}

export interface ContactColumn {
  id: ContactColumnId;
  label: string;
  sortable: boolean; // true ⇒ id is a SortField (api ALLOWED_SORT_FIELDS)
  visible: (v: ColVis) => boolean;
}

// Order is the on-screen column order. ContactRow renders its cells in this
// same order — keep the two in lockstep.
export const CONTACTS_COLUMNS: ContactColumn[] = [
  { id: 'created_at', label: 'Time', sortable: true, visible: () => true },
  { id: 'phone', label: 'Phone', sortable: true, visible: () => true },
  { id: 'source', label: 'From', sortable: true, visible: () => true },
  { id: 'channel', label: 'Path', sortable: true, visible: () => true },
  { id: 'campaign', label: 'Campaign', sortable: true, visible: () => true },
  { id: 'status', label: 'Status', sortable: true, visible: () => true },
  { id: 'conversion', label: 'Conversion', sortable: false, visible: () => true },
  { id: 'client', label: 'Client', sortable: false, visible: (v) => v.showClient },
  { id: 'line', label: 'Received on', sortable: false, visible: (v) => v.showYourNumber },
];

export const visibleColumns = (v: ColVis): ContactColumn[] =>
  CONTACTS_COLUMNS.filter((c) => c.visible(v));

export const columnCount = (v: ColVis): number => visibleColumns(v).length;

// ─── Helpers ──────────────────────────────────────────────────────────

// Date-preset → { date_from, date_to } ISO strings. 'all' returns neither.
// 'custom' is driven by `customRange` state instead.
export function presetToRange(
  preset: DatePreset,
  customRange: DateRange | undefined,
): { date_from?: string; date_to?: string } {
  const now = new Date();
  switch (preset) {
    case '24h':
      return {
        date_from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      };
    case '7d':
      return {
        date_from: new Date(
          now.getTime() - 7 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      };
    case '30d':
      return {
        date_from: new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      };
    case 'month':
      return {
        date_from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      };
    case 'lastmonth':
      return {
        date_from: new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        ).toISOString(),
        date_to: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      };
    case 'custom':
      // From-only when user picks a single date; both when a range.
      return {
        date_from: customRange?.from?.toISOString(),
        date_to: customRange?.to?.toISOString(),
      };
    case 'all':
      return {};
  }
}

// Map segment chip to a predicate. Status segments match on contact.status
// directly (Segment status ids = ContactStatus tokens); platform segments
// classify (source, medium) via getSourceLabel.
export function segmentMatches(segment: Segment, c: Contact): boolean {
  if (segment === 'all') return true;
  if (segment === 'purchase') return c.status === 'purchase';
  if (segment === 'qualified') return c.status === 'qualified';
  if (segment === 'new_lead') return c.status === 'new_lead';
  const label = getSourceLabel(c.source, c.medium);
  if (segment === 'meta') return label === 'Meta Ads';
  if (segment === 'google') return label === 'Google Ads';
  return true;
}

// Full timestamp for the expanded card metadata strip. "27 May 2026, 12:34"
export function formatFullTimestamp(iso: string): string {
  const d = new Date(iso);
  const datePart = d.toLocaleDateString(UI_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString(UI_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart}, ${timePart}`;
}

// CSV export — column order mirrors the documented contacts download. Headers
// + escaping unchanged so any existing customer download habit stays consistent.
export function exportToCsv(contacts: Contact[], showClient: boolean) {
  const headers = [
    'Date',
    'Time',
    'Phone',
    'Status',
    'Value',
    'From',
    'Path',
    'Campaign',
    ...(showClient ? ['Client'] : []),
    'Line',
    'Notes',
    'Source',
    'Medium',
    'Channel',
    'Endpoint',
    'Term',
    'Content',
    'WhatsApp Name',
    'Landing Page',
    'City',
    'Country',
    'GCLID',
    'FBCLID',
  ];

  // Free-tier: locked rows have identity stripped server-side and are not
  // exportable — the ownership promise covers the visible contacts
  // (PLAN_FREE_TIER_REDESIGN.md §5).
  const rows = contacts.filter((c) => !c.locked).map((c) => {
    const d = new Date(c.created_at);
    const base: (string | number)[] = [
      d.toLocaleDateString(UI_LOCALE),
      d.toLocaleTimeString(UI_LOCALE, { hour: '2-digit', minute: '2-digit' }),
      c.phone,
      STATUS_LABELS[c.status],
      c.value != null ? c.value : '',
      getSourceLabel(c.source, c.medium),
      getChannelLabel(c.channel, c.endpoint),
      c.campaign || '',
    ];
    if (showClient) base.push(c.client_name || '');
    return [
      ...base,
      c.tracking_number || '',
      c.notes || '',
      c.source,
      c.medium || '',
      c.channel || '',
      c.endpoint || '',
      c.term || '',
      c.content || '',
      c.profile_name || '',
      c.landing_page || '',
      c.from_city || '',
      c.from_country || '',
      c.gclid || '',
      c.fbclid || '',
    ];
  });

  const csv = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
    )
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
